"use strict";

type KrunkerConfig = typeof import("../src/config.js");
type KrunkerUtils = typeof import("../src/libs/utils.js");
type RewriteInput = string | URL | Request | null | undefined;
type UnlockRecord = Record<string, any>;

const LEVEL_LOCK_KEYS = [
  "minRec",
  "minLvl",
  "minLevel",
  "levelReq",
  "reqLevel",
  "reqLvl",
  "unlockLevel",
  "lockedLevel",
];
const LEVEL_REQ_TEXT = /\b(?:level|lvl)\b/i;

function isRecord(value: unknown): value is UnlockRecord {
  return !!value && typeof value === "object";
}

function eachEntry(value: unknown, cb: (entry: UnlockRecord) => void): void {
  if (Array.isArray(value)) {
    for (const entry of value) if (isRecord(entry)) cb(entry);
    return;
  }
  if (!isRecord(value)) return;
  for (const key of Object.keys(value)) {
    const entry = value[key];
    if (isRecord(entry)) cb(entry);
  }
}

function clearNumericLevelLocks(entry: UnlockRecord): boolean {
  let changed = false;
  for (const key of LEVEL_LOCK_KEYS) {
    if (!(key in entry)) continue;
    if (entry[key] === 0 || entry[key] == null) continue;
    entry[key] = 0;
    changed = true;
  }
  return changed;
}

function clearLevelReqCallback(entry: UnlockRecord): void {
  const reqText =
    typeof entry.reqT === "string"
      ? entry.reqT
      : typeof entry.reqText === "string"
        ? entry.reqText
        : typeof entry.requirement === "string"
          ? entry.requirement
          : "";
  if (!LEVEL_REQ_TEXT.test(reqText)) return;
  if (typeof entry.req === "function") entry.req = () => true;
  if ("reqT" in entry) entry.reqT = "";
  if ("reqText" in entry) entry.reqText = "";
  if ("requirement" in entry) entry.requirement = "";
}

function clearLevelLocks(
  value: unknown,
  options: { clearReqCallbacks?: boolean } = {},
): void {
  eachEntry(value, (entry) => {
    clearNumericLevelLocks(entry);
    if (options.clearReqCallbacks) clearLevelReqCallback(entry);
  });
}

function clearStoreLevelLocks(store: unknown): void {
  if (!isRecord(store)) return;
  clearLevelLocks(store.skins, { clearReqCallbacks: true });
  clearLevelLocks(store.cosmetics, { clearReqCallbacks: true });
}

function applyUnlockDataHooks(game: UnlockRecord): void {
  clearLevelLocks(game.weapons);
  clearLevelLocks(game.weaponConfig);
  clearLevelLocks(game.classes);
  clearLevelLocks(game.attach);
  clearLevelLocks(game.reticles);
  clearStoreLevelLocks(game.store);
}

export function doUnlockLevelRequirementHooks(game: unknown): void {
  if (!isRecord(game)) return;
  applyUnlockDataHooks(game);

  const applyConfig = game.applyConfig;
  if (typeof applyConfig !== "function" || applyConfig.__unlockWrapped) return;
  const wrapped = function (this: UnlockRecord, ...args: unknown[]) {
    const result = applyConfig.apply(this, args);
    applyUnlockDataHooks(this);
    return result;
  };
  wrapped.__unlockWrapped = true;
  game.applyConfig = wrapped;
}

export function doConfigUnlockLevelRequirementHooks(config: unknown): void {
  if (!isRecord(config)) return;
  clearLevelLocks(config.skinColors, { clearReqCallbacks: true });
}

export function doConfigHooks(config: KrunkerConfig): void {
  config.regionNames["us-va"] = "Virginia";
  config.regionNames["us-ny"] = "New York";
  config.regionNames["us-nj"] = "New Jersey";
  config.regionNames["eu-fr"] = "Frankfurt";
  doConfigUnlockLevelRequirementHooks(config);
}

export function doUTILSHooks(UTILS: KrunkerUtils): void {
  const { copyToClipboard } = UTILS;
  UTILS.copyToClipboard = function (this: KrunkerUtils, text: string): void {
    // fix copying http/s link
    copyToClipboard.call(
      this,
      text.replace("https://" + location.host, location.origin),
    );
  };
}

export function rewrite(url: string): string {
  if (url.startsWith("/")) {
    url = url.slice(1);
  }
  {
    const p = "../";
    if (url.startsWith(p)) return url.replace(p, "");
  }
  {
    const p = "file:///";
    if (url.startsWith(p)) return url.replace(p, "");
  }
  {
    const p = "https://user-assets.krunker.io/";
    // console.log("rewriting user asset", url);
    if (url.startsWith(p)) return url.replace(p, "user-assets/");
  }
  {
    const p = "https://assets.krunker.io/";
    if (url.startsWith(p)) return url.replace(p, "");
  }
  // {
  //   const p = "/textures/";
  //   if (url.startsWith(p)) return url.replace(p, "https://assets.krunker.zip/textures/");
  // }
  // {
  //   const p = "https://totallynotio.krunker.zip/textures/";
  //   if (url.startsWith(p)) return url.replace(p, "https://assets.krunker.zip/textures/");
  // }
  return url;
}

function trimEndpoint(url: string): string {
  return url.replace(/\/+$/, "");
}

function replaceEndpointPrefix(
  url: string,
  prefix: string,
  target: string,
): string | null {
  const cleanPrefix = trimEndpoint(prefix);
  if (
    url !== cleanPrefix &&
    !url.startsWith(cleanPrefix + "/") &&
    !url.startsWith(cleanPrefix + "?")
  ) {
    return null;
  }
  return trimEndpoint(target) + url.slice(cleanPrefix.length);
}

function rewriteConnectEndpoint(url: string): string | null {
  const state = window.__krunkerConnectState;
  if (!state?.matchmakerURL || !state.socialURL || !state.apiURL) return null;

  const matchmakerPrefixes = [
    "http://true",
    "https://matchmaker.krunker.io",
    "https://matchmaker_beta.krunker.io",
    "https://matchmaker.krunker.zip",
    location.origin + "/mm",
    "/mm",
  ];
  const socialPrefixes = [
    "https://social.krunker.io",
    "https://social_beta.krunker.io",
    "https://social.krunker.zip",
    location.origin + "/social",
    "/social",
  ];
  const apiPrefixes = [
    "https://api.krunker.io",
    "https://api_beta.krunker.io",
    "https://api.krunker.zip",
    "https://krunker.io",
    location.origin + "/api",
    "/api",
  ];

  for (const prefix of matchmakerPrefixes) {
    const rewritten = replaceEndpointPrefix(url, prefix, state.matchmakerURL);
    if (rewritten) return rewritten;
  }
  for (const prefix of socialPrefixes) {
    const rewritten = replaceEndpointPrefix(url, prefix, state.socialURL);
    if (rewritten) return rewritten;
  }
  for (const prefix of apiPrefixes) {
    const rewritten = replaceEndpointPrefix(url, prefix, state.apiURL);
    if (rewritten) return rewritten;
  }
  return null;
}

function getLegacyRewriteBase(): string {
  return typeof server === "string" && server ? server : location.origin;
}

export function rewrite_mm(url: string): string;
export function rewrite_mm<T extends Exclude<RewriteInput, string>>(url: T): T;
export function rewrite_mm(url: RewriteInput): RewriteInput | string;
export function rewrite_mm(url: RewriteInput): RewriteInput {
  if (typeof url !== "string") return url;
  let next = url;
  const connectUrl = rewriteConnectEndpoint(next);
  if (connectUrl) return connectUrl;
  const legacyBase = getLegacyRewriteBase();
  {
    const p = "../";
    if (next.startsWith(p)) return next.replace(p, "");
  }
  {
    const p = "http://true";
    if (next.startsWith(p)) {
      next = next.replace(p, legacyBase);
    }
  }
  {
    const p = "https://krunker.io";
    if (next.startsWith(p)) return next.replace(p, legacyBase);
  }
  {
    const p = "https://social_beta.krunker.io";
    if (next.startsWith(p)) return next.replace(p, legacyBase);
  }
  {
    const p = "https://matchmaker_beta.krunker.io";
    if (next.startsWith(p)) return next.replace(p, legacyBase);
  }
  {
    const p = "https://assets.krunker.io/";
    if (next.startsWith(p)) return next.replace(p, "");
  }
  if (next.startsWith("/")) return next.slice(1);
  return next;
}
